import { ReactNode } from 'react';

export const stopEventPropagation = (e): void => {
    // disable pasting block when block is copied to clipboard
    e.stopPropagation();
};

export function preventDefault(e: Event): void {
    e.preventDefault();
}

export function falseNotZero(value: any) {
    return !value && value !== 0;
}

export const isReactChildrenHasContent = (children: ReactNode) => {
    return Array.isArray(children)
        ? !children.every(falseNotZero)
        : !falseNotZero(children);
};

export function isFunction(fn) {
    return typeof fn === 'function';
}

export function isObject(obj): obj is object {
    return obj instanceof Object;
}

export function isUrl(str) {
    return str ? !!str.match(/^https?:\//) : false;
}

export function guid() {
    const s4 = () => {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    };
    //return id of format 'aaaaaaaa'-'aaaa'-'aaaa'-'aaaa'-'aaaaaaaaaaaa'
    return (
        s4() +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        '-' +
        s4() +
        s4() +
        s4()
    );
}

/** Used to map characters to HTML entities. */
const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
};

/** Used to match HTML entities and HTML characters. */
const reUnescapedHtml = /[&<>"']/g;
const reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

/**
 * Converts the characters "&", "<", ">", '"', and "'" in `string` to their
 * corresponding HTML entities.
 *
 * **Note:** No other characters are escaped. To escape additional
 * characters use a third-party library like [_he_](https://mths.be/he).
 *
 * Though the ">" character is escaped for symmetry, characters like
 * ">" and "/" don't need escaping in HTML and have no special meaning
 * unless they're part of a tag or unquoted attribute value. See
 * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
 * (under "semi-related fun fact") for more details.
 *
 * When working with HTML you should always
 * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
 * XSS vectors.
 *
 * @since 0.1.0
 * @category String
 * @param {string} [string=''] The string to escape.
 * @returns {string} Returns the escaped string.
 * @see escapeRegExp, unescape
 * @example
 *
 * escape('fred, barney, & pebbles')
 * // => 'fred, barney, &amp; pebbles'
 */
export function escape(string) {
    return string && reHasUnescapedHtml.test(string)
        ? string.replace(reUnescapedHtml, chr => htmlEscapes[chr])
        : string || '';
}
/**
 * @deprecated Use `HBPhoneInput` from `@honeybook/hbui`, which formats phone numbers
 * for all countries via libphonenumber-js. This utility only handles US format
 * and will be removed in a future major version.
 */
export const formatPhoneNumber = value => {
    // return nothing if no value
    if (!value) {
        return value;
    }

    // only allows 0-9 inputs
    const currentValue = value.replace(/[^\d]/g, '');
    const cvLength = currentValue.length;

    if (cvLength > 0) {
        // returns: "(xxx)", "(xxx) x", "(xxx) xx", "(xxx) xxx",
        if (cvLength > 3 && cvLength < 7) {
            return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
        }

        if (cvLength > 6) {
            // returns: "(xxx) xxx-", (xxx) xxx-x", "(xxx) xxx-xx", "(xxx) xxx-xxx", "(xxx) xxx-xxxx"
            return `(${currentValue.slice(0, 3)}) ${currentValue.slice(
                3,
                6
            )}-${currentValue.slice(6, 15)}`;
        }
    }
    return currentValue;
};

export const roundToDecimal = (num: number, decimal: number): number => {
    const decimalValue = 10 ** decimal;
    return Math.round(num * decimalValue) / decimalValue;
};

export const inertAttr = ({ inert }: { inert: boolean }) =>
    ({
        inert: inert ? 'true' : undefined
    } as unknown as {
        inert: boolean;
    });
